<template>
  <div id="broker-modal">
    <v-dialog :value="isModalActive" max-width="600" @input="inputModal">
      <v-card
        class="broker-modal-card pb-1"
        :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : undefined"
      >
        <v-card-title>
          {{ $t('views.brokers.brokerAccounts.brokerModal.title') }}
        </v-card-title>

        <v-card-text class="py-0 pt-3">
          <ApiErrorAlert ref="apiErrorAlert" :error="error" />

          <v-form>
            <v-row>
              <v-col v-if="!editing" cols="12" md="12">
                <v-autocomplete
                  v-model="broker.code"
                  hide-details
                  :items="predefinedBrokers"
                  :disabled="loading || broker.custom"
                  hide-no-data
                  :label="$t(`views.brokers.brokerAccounts.brokerModal.predefinedBroker.label`)"
                  :placeholder="
                    $t(`views.brokers.brokerAccounts.brokerModal.predefinedBroker.placeholder`)
                  "
                ></v-autocomplete>
                <p class="mt-2 mb-0 pb-0">
                  <a @click="toogleCustom">
                    {{
                      !broker.custom
                        ? $t(`views.brokers.brokerAccounts.brokerModal.activateCustom`)
                        : $t(`views.brokers.brokerAccounts.brokerModal.deactivateCustom`)
                    }}
                  </a>
                </p>
              </v-col>

              <template v-if="broker.custom || editing">
                <v-col cols="12" md="12">
                  <div v-if="!editing" class="my-2 create-custom-broker-title">
                    {{ $t(`views.brokers.brokerAccounts.brokerModal.custom.createCustomBroker`) }}
                  </div>
                  <template v-if="!editing">
                    <div class="create-custom-broker-description">
                      {{ $t(`views.brokers.brokerAccounts.brokerModal.custom.description`) }}
                    </div>
                    <v-text-field
                      v-model="broker.name"
                      :label="$t(`views.brokers.brokerAccounts.brokerModal.custom.name`)"
                      :disabled="loading || editing"
                      class="mt-3"
                      hide-details
                      maxLength="100"
                    />
                  </template>
                  <div v-else>
                    <h2>{{ $t(`views.brokers.brokerAccounts.brokerModal.custom.name`) }}</h2>
                    {{ broker.name }}
                  </div>
                </v-col>
                <v-col cols="12" md="12" class="mt-2 pt-0">
                  <CountrySelector
                    v-if="!editing"
                    v-model="broker.country"
                    :countries="allCountries"
                    :label="$t(`views.brokers.brokerAccounts.brokerModal.custom.country`)"
                    :disabled="loading || editing"
                    hide-details
                  />
                  <div v-else>
                    <h2>{{ $t(`views.brokers.brokerAccounts.brokerModal.custom.country`) }}</h2>
                    {{ broker.country ? $t(`common.countries.${broker.country}`) : '' }}
                  </div>
                </v-col>
              </template>

              <template v-if="broker.custom || editing">
                <v-col cols="12" md="12" class="my-0 py-0">
                  <div class="items-flex">
                    <v-switch
                      v-model="broker.multiCurrency"
                      :disabled="loading"
                      :label="$t(`views.brokers.brokerAccounts.brokerModal.multiCurrency`)"
                      hide-details
                      class="mr-3"
                    ></v-switch>
                    <InfoIcon
                      :text="$t(`views.brokers.brokerAccounts.brokerModal.multiCurrencyInfo`)"
                    />
                  </div>
                </v-col>
              </template>

              <template v-if="broker.custom || editing">
                <v-col cols="12" md="12" class="my-0 py-0">
                  <div class="items-flex">
                    <v-switch
                      v-model="broker.destinationRetention"
                      :disabled="loading"
                      :label="$t(`views.brokers.brokerAccounts.brokerModal.destinationRetention`)"
                      hide-details
                      class="mr-3"
                    ></v-switch>
                    <InfoIcon
                      :text="
                        $t(`views.brokers.brokerAccounts.brokerModal.destinationRetentionInfo`)
                      "
                    />
                  </div>
                </v-col>
              </template>

              <v-col v-if="broker.code || broker.custom" cols="12" md="12" class="mt-3 py-5">
                <div class="mb-3 mt-0">
                  <span v-if="!editing" class="mr-2 alias-description-1">
                    {{ $t(`views.brokers.brokerAccounts.brokerModal.alias.description1`) }}
                  </span>
                  <span v-else class="mr-2 alias-description-1">
                    {{ $t(`views.brokers.brokerAccounts.brokerModal.alias.description1editing`) }}
                  </span>
                  <br />
                  <span class="mr-2 alias-description-2">
                    {{ $t(`views.brokers.brokerAccounts.brokerModal.alias.description2`) }}
                  </span>
                </div>

                <v-text-field
                  v-model="broker.alias"
                  :label="$t(`views.brokers.brokerAccounts.brokerModal.alias.label`)"
                  :disabled="loading"
                  hide-details
                  class="small-input"
                  maxLength="100"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <ModalButtons
          :editing="editing"
          :loading="loading"
          :saveText="$t('views.brokers.brokerAccounts.brokerModal.save')"
          :saveDisabled="saveDisabled"
          @delete="confirmDelete"
          @cancel="closeModal"
          @save="saveBroker"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { pick, get, find } from 'lodash'
import { createBroker, editBroker, deleteBroker } from '@/lib/api.js'
import predefinedBrokers from '@/lib/brokers/predefinedBrokers.json'
import { allCountries } from '@/lib/countries/countries.js'
import modalMixin from '@/views/components/core/modalMixin.js'
import CountrySelector from '@/views/components/common/CountrySelector'
import InfoIcon from '@/views/components/common/InfoIcon'
import ApiErrorAlert from '@/views/components/common/ApiErrorAlert'
import gtag from '@/lib/gtag'

export default {
  components: { InfoIcon, CountrySelector, ApiErrorAlert },
  mixins: [modalMixin],
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    editItem: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    broker: null,
    loading: false,
    allCountries,
    error: null,
  }),
  computed: {
    ...mapState(['user']),
    predefinedBrokers() {
      return predefinedBrokers.brokers.map((broker) => {
        return {
          text: broker.name,
          value: broker.code,
        }
      })
    },
    saveDisabled() {
      if (!this.broker.alias) return true
      if (!this.broker.custom) {
        return !this.broker.code
      } else {
        return !this.broker.name || !this.broker.country
      }
    },
  },
  watch: {
    editItem: {
      handler() {
        if (this.editing) {
          this.broker = JSON.parse(JSON.stringify(this.editItem))
        } else {
          this.resetForm()
        }
      },
      immediate: true,
      deep: true,
    },
    'broker.custom'() {
      this.$set(this.broker, 'code', null)
    },
    'broker.code'() {
      this.selectDefaultDestinationRetention()
      this.selectDefaultMultiCurrency()

      if (
        !this.editing &&
        this.broker.code &&
        !this.broker.custom &&
        predefinedBrokers.brokers &&
        predefinedBrokers.brokers.length
      ) {
        const thisBroker = predefinedBrokers.brokers.find((elem) => {
          return elem.code === this.broker.code
        })
        if (thisBroker) this.$set(this.broker, 'alias', thisBroker.name)
      }
    },
    'broker.country'() {
      this.selectDefaultDestinationRetention()
    },
  },
  methods: {
    selectDefaultDestinationRetention() {
      if (this.editing) return

      if (this.broker.custom) {
        this.$set(this.broker, 'destinationRetention', this.broker.country === this.user.country)
      } else {
        const predefinedBroker = find(predefinedBrokers.brokers, { code: this.broker.code })
        const defaultDestinationRetentionByCountry = get(
          predefinedBroker,
          `defaultDestinationRetentionByCountry.${this.user.country}`,
          false
        )
        this.$set(this.broker, 'destinationRetention', defaultDestinationRetentionByCountry)
      }
    },
    selectDefaultMultiCurrency() {
      if (this.editing) return

      if (this.broker.custom) {
        this.$set(this.broker, 'multiCurrency', false)
      } else {
        const predefinedBroker = find(predefinedBrokers.brokers, { code: this.broker.code })
        const defaultMultiCurrency = get(predefinedBroker, 'defaultMultiCurrency', false)
        this.$set(this.broker, 'multiCurrency', defaultMultiCurrency)
      }
    },
    toogleCustom() {
      if (this.loading) return
      this.$set(this.broker, 'custom', !this.broker.custom)
    },
    resetForm() {
      this.$set(this, 'broker', {
        custom: false,
        multiCurrency: false,
        destinationRetention: false,
      })
      this.loading = false
      this.error = null
    },
    async saveBroker() {
      this.loading = true
      let broker = Object.assign({}, this.broker)
      try {
        if (this.editing) {
          broker = pick(this.broker, ['alias', 'multiCurrency', 'destinationRetention'])
          await editBroker(this.broker._id, broker)
        } else {
          if (!broker.custom)
            broker = pick(this.broker, [
              'alias',
              'custom',
              'code',
              'destinationRetention',
              'multiCurrency',
            ])
          else
            broker = pick(this.broker, [
              'alias',
              'custom',
              'name',
              'country',
              'multiCurrency',
              'destinationRetention',
              'multiCurrency',
            ])
          await createBroker(broker)
        }
        this.$emit('saved', broker)
        this.closeModal()

        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })

        gtag.pageview({
          pagePath: `/brokerAccounts/${this.editing ? 'edit' : 'create'}/success`,
          pageTitle: `brokerAccounts|${this.editing ? 'edit' : 'create'}|success`,
          pageContentGroup: 'modal',
        })
      } catch (error) {
        this.error = get(error, 'data.name', null)
        if (!this.error) {
          this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        } else {
          this.$refs.apiErrorAlert.scrollToError()
        }

        gtag.pageview({
          pagePath: `/brokerAccounts/${this.editing ? 'edit' : 'create'}/error`,
          pageTitle: `brokerAccounts|${this.editing ? 'edit' : 'create'}|error`,
          pageContentGroup: 'modal',
        })
      }
      this.loading = false
    },
    async confirmDelete() {
      if (
        await this.$root.$confirm({
          title: this.$t('views.brokers.brokerAccounts.confirm.title'),
          message: this.$t('views.brokers.brokerAccounts.confirm.message'),
        })
      ) {
        this.loading = true
        try {
          await deleteBroker(this.broker)
          this.$emit('saved')
          this.closeModal()

          this.$root.$snackbar.success({ text: this.$t('common.deleteOk') })
          gtag.pageview({
            pagePath: `/brokerAccounts/delete/success`,
            pageTitle: `brokerAccounts|delete|success`,
            pageContentGroup: 'modal',
          })
        } catch (error) {
          this.$root.$snackbar.error({ text: this.$t('common.deleteError') })
          gtag.pageview({
            pagePath: `/brokerAccounts/delete/error`,
            pageTitle: `brokerAccounts|delete|error`,
            pageContentGroup: 'modal',
          })
        }
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
.broker-modal-card {
  h1 {
    font-size: 17px;
    font-weight: 400;
  }
  h2 {
    font-size: 14px;
    font-weight: 500;
  }
  a {
    font-size: 13px;
  }
  .create-custom-broker-title {
    font-size: 14px;
    font-weight: 500;
  }
  .create-custom-broker-description {
    font-size: 13px;
    line-height: 1.6;
    font-weight: 300;
  }
  .alias-description-1 {
    font-size: 15px;
    font-weight: 500;
  }
  .alias-description-2 {
    font-size: 13px;
  }
  .items-flex {
    display: flex;
    align-items: flex-end;
  }
  .small-input {
    max-width: 250px;
  }
}
</style>
